<template>
  <div class="create-status-page">
    <!-- Header -->
    <div class="header">
      <button class="back-button" @click="goBack">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <h1>{{ $t("stories.add_status") }}</h1>
      <div class="header-spacer"></div>
    </div>

    <!-- Content -->
    <div class="content">
      <!-- Status Type Selector -->
      <div class="type-selector">
        <button
          v-for="type in statusTypes"
          :key="type.value"
          class="type-button"
          :class="{ active: selectedType === type.value }"
          @click="selectedType = type.value"
        >
          <component :is="type.icon" />
          <span>{{ type.label }}</span>
        </button>
      </div>

      <!-- Text Status Form -->
      <div v-if="selectedType === 'text'" class="form-section">
        <div class="text-preview" :style="textPreviewStyle">
          <p>{{ textOverlay || $t("stories.your_text_here") }}</p>
        </div>

        <div class="input-group">
          <label>{{ $t("stories.text_content") }}</label>
          <textarea
            v-model="textOverlay"
            :placeholder="$t('stories.enter_your_text')"
            maxlength="500"
            rows="4"
          ></textarea>
          <span class="char-count">{{ textOverlay.length }}/500</span>
        </div>

        <div class="input-group">
          <label
            >{{ $t("stories.description") }} ({{
              $t("stories.optional")
            }})</label
          >
          <textarea
            v-model="description"
            :placeholder="$t('stories.add_description')"
            maxlength="1000"
            rows="2"
          ></textarea>
        </div>
      </div>

      <!-- Image Status Form -->
      <div v-else-if="selectedType === 'image'" class="form-section">
        <div
          class="media-upload-area"
          :class="{ 'has-preview': imagePreview }"
          @click="triggerFileInput('image')"
          @dragover.prevent
          @drop.prevent="handleDrop($event, 'image')"
        >
          <img
            v-if="imagePreview"
            :src="imagePreview"
            class="media-preview"
            alt="Preview"
          />
          <div v-else class="upload-placeholder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <p>{{ $t("stories.click_or_drag_image") }}</p>
          </div>
        </div>

        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          hidden
          @change="handleFileSelect($event, 'image')"
        />

        <div class="input-group">
          <label
            >{{ $t("stories.text_overlay") }} ({{
              $t("stories.optional")
            }})</label
          >
          <input
            v-model="textOverlay"
            type="text"
            :placeholder="$t('stories.add_text_overlay')"
            maxlength="200"
          />
        </div>

        <div class="input-group">
          <label
            >{{ $t("stories.description") }} ({{
              $t("stories.optional")
            }})</label
          >
          <textarea
            v-model="description"
            :placeholder="$t('stories.add_description')"
            maxlength="1000"
            rows="2"
          ></textarea>
        </div>
      </div>

      <!-- Video Status Form -->
      <div v-else-if="selectedType === 'video'" class="form-section">
        <div
          class="media-upload-area"
          :class="{ 'has-preview': videoPreview }"
          @click="triggerFileInput('video')"
          @dragover.prevent
          @drop.prevent="handleDrop($event, 'video')"
        >
          <video
            v-if="videoPreview"
            :src="videoPreview"
            class="media-preview"
            controls
          ></video>
          <div v-else class="upload-placeholder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            <p>{{ $t("stories.click_or_drag_video") }}</p>
          </div>
        </div>

        <input
          ref="videoInput"
          type="file"
          accept="video/*"
          hidden
          @change="handleFileSelect($event, 'video')"
        />

        <div class="input-group">
          <label
            >{{ $t("stories.text_overlay") }} ({{
              $t("stories.optional")
            }})</label
          >
          <input
            v-model="textOverlay"
            type="text"
            :placeholder="$t('stories.add_text_overlay')"
            maxlength="200"
          />
        </div>

        <div class="input-group">
          <label
            >{{ $t("stories.description") }} ({{
              $t("stories.optional")
            }})</label
          >
          <textarea
            v-model="description"
            :placeholder="$t('stories.add_description')"
            maxlength="1000"
            rows="2"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="footer">
      <button
        class="submit-button"
        :disabled="!canSubmit || isSubmitting"
        @click="submitStatus"
      >
        <span v-if="isSubmitting" class="spinner"></span>
        <span v-else>{{ $t("stories.publish_status") }}</span>
      </button>
    </div>

    <!-- Error Toast -->
    <Transition name="slide-up">
      <div v-if="errorMessage" class="error-toast">
        {{ errorMessage }}
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, h } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";
import { StoriesApi } from "@/services/storiesApi";

const router = useRouter();
const { t } = useI18n();

// Configure SweetAlert2 Toast for success notifications - minimal and elegant
const Toast = Swal.mixin({
  toast: true,
  position: "top",
  showConfirmButton: false,
  showCloseButton: false,
  timer: 3500,
  timerProgressBar: false,
  backdrop: false,
  allowOutsideClick: true,
  showClass: {
    popup: "swal2-show status-toast-animate-in",
  },
  hideClass: {
    popup: "swal2-hide status-toast-animate-out",
  },
  customClass: {
    popup: "status-success-toast-minimal",
    title: "status-toast-title",
    icon: "status-toast-icon",
  },
});

// Icons as render functions
const TextIcon = () =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: 24,
      height: 24,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": 2,
    },
    [
      h("polyline", { points: "4 7 4 4 20 4 20 7" }),
      h("line", { x1: 9, y1: 20, x2: 15, y2: 20 }),
      h("line", { x1: 12, y1: 4, x2: 12, y2: 20 }),
    ]
  );

const ImageIcon = () =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: 24,
      height: 24,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": 2,
    },
    [
      h("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2, ry: 2 }),
      h("circle", { cx: 8.5, cy: 8.5, r: 1.5 }),
      h("polyline", { points: "21 15 16 10 5 21" }),
    ]
  );

const VideoIcon = () =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: 24,
      height: 24,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": 2,
    },
    [
      h("polygon", { points: "23 7 16 12 23 17 23 7" }),
      h("rect", { x: 1, y: 5, width: 15, height: 14, rx: 2, ry: 2 }),
    ]
  );

// State
const selectedType = ref("text");
const textOverlay = ref("");
const description = ref("");
const mediaFile = ref(null);
const imagePreview = ref(null);
const videoPreview = ref(null);
const isSubmitting = ref(false);
const errorMessage = ref("");

// Refs
const imageInput = ref(null);
const videoInput = ref(null);

// Status types - labels will be computed with i18n
const statusTypes = computed(() => [
  { value: "text", label: t("stories.text"), icon: TextIcon },
  { value: "image", label: t("stories.image"), icon: ImageIcon },
  { value: "video", label: t("stories.video"), icon: VideoIcon },
]);

// Computed
const canSubmit = computed(() => {
  if (selectedType.value === "text") {
    return textOverlay.value.trim().length > 0;
  }
  return mediaFile.value !== null;
});

const textPreviewStyle = computed(() => ({
  background: "linear-gradient(135deg, #00c444 0%, #00a43c 50%, #008836 100%)",
}));

// Methods
function goBack() {
  router.back();
}

function triggerFileInput(type) {
  if (type === "image") {
    imageInput.value?.click();
  } else {
    videoInput.value?.click();
  }
}

function handleFileSelect(event, type) {
  const file = event.target.files?.[0];
  if (!file) return;

  processFile(file, type);
}

function handleDrop(event, type) {
  const file = event.dataTransfer.files?.[0];
  if (!file) return;

  // Validate file type
  if (type === "image" && !file.type.startsWith("image/")) {
    showError(t("stories.please_select_image"));
    return;
  }
  if (type === "video" && !file.type.startsWith("video/")) {
    showError(t("stories.please_select_video"));
    return;
  }

  processFile(file, type);
}

function processFile(file, type) {
  // Validate file size (max 50MB for video, 10MB for image)
  const maxSize = type === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
  const sizeLabel = type === "video" ? "50MB" : "10MB";
  if (file.size > maxSize) {
    showError(t("stories.file_too_large", { size: sizeLabel }));
    return;
  }

  mediaFile.value = file;

  // Create preview
  const reader = new FileReader();
  reader.onload = (e) => {
    if (type === "image") {
      imagePreview.value = e.target.result;
      videoPreview.value = null;
    } else {
      videoPreview.value = e.target.result;
      imagePreview.value = null;
    }
  };
  reader.readAsDataURL(file);
}

function showError(message) {
  errorMessage.value = message;
  setTimeout(() => {
    errorMessage.value = "";
  }, 3000);
}

async function submitStatus() {
  if (!canSubmit.value || isSubmitting.value) return;

  isSubmitting.value = true;
  errorMessage.value = "";

  try {
    await StoriesApi.createStatus({
      type: selectedType.value,
      mediaFile: selectedType.value !== "text" ? mediaFile.value : null,
      textOverlay: textOverlay.value.trim() || null,
      description: description.value.trim() || null,
    });

    // Show success toast with admin review message
    Toast.fire({
      icon: "success",
      title: t("stories.submitted_for_review"),
      iconColor: "#00c444",
    });

    // Navigate back
    router.replace({ path: router.currentRoute.value.query.returnTo || "/" });
  } catch (error) {
    showError(error.message || t("stories.failed_to_create"));
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.create-status-page {
  min-height: 100vh;
  background: var(--surface, #fffbfe);
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--outline-variant, #cac4d0);
  background: var(--surface, #fffbfe);
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--on-surface, #1c1b1f);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.back-button:hover {
  background: var(--surface-container, #f3edf7);
}

.header h1 {
  flex: 1;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--on-surface, #1c1b1f);
  margin: 0;
}

.header-spacer {
  width: 40px;
}

/* Content */
.content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

/* Type Selector */
.type-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.type-button {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--surface-container, #f3edf7);
  border: 2px solid transparent;
  border-radius: 12px;
  color: var(--on-surface-variant, #49454f);
  cursor: pointer;
  transition: all 0.2s ease;
}

.type-button:hover {
  background: var(--surface-container-high, #ece6f0);
}

.type-button.active {
  background: linear-gradient(145deg, #e8fff1 0%, #d4f7e4 100%);
  border-color: #00c444;
  color: #00873a;
}

.type-button span {
  font-size: 14px;
  font-weight: 500;
}

/* Form Section */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Text Preview */
.text-preview {
  aspect-ratio: 9/16;
  max-height: 300px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
}

.text-preview p {
  font-size: 20px;
  font-weight: bold;
  color: white;
  text-align: center;
  word-wrap: break-word;
}

/* Media Upload Area */
.media-upload-area {
  aspect-ratio: 9/16;
  max-height: 400px;
  border: 2px dashed var(--outline, #79747e);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  background: var(--surface-container, #f3edf7);
}

.media-upload-area:hover {
  border-color: #00c444;
  background: linear-gradient(145deg, #e8fff1 0%, #f0fff5 100%);
}

.media-upload-area.has-preview {
  border-style: solid;
  border-color: #00c444;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--on-surface-variant, #49454f);
}

.upload-placeholder p {
  font-size: 14px;
}

.media-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Input Group */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--on-surface, #1c1b1f);
}

.input-group input,
.input-group textarea {
  padding: 12px 16px;
  background: var(--surface-container, #f3edf7);
  border: 1px solid var(--outline-variant, #cac4d0);
  border-radius: 12px;
  font-size: 14px;
  color: var(--on-surface, #1c1b1f);
  resize: none;
  transition: border-color 0.2s ease;
}

.input-group input:focus,
.input-group textarea:focus {
  outline: none;
  border-color: #00c444;
  box-shadow: 0 0 0 2px rgba(0, 196, 68, 0.15);
}

.input-group input::placeholder,
.input-group textarea::placeholder {
  color: var(--on-surface-variant, #49454f);
}

.char-count {
  font-size: 12px;
  color: var(--on-surface-variant, #49454f);
  text-align: right;
}

/* Footer */
.footer {
  padding: 16px;
  border-top: 1px solid var(--outline-variant, #cac4d0);
  background: var(--surface, #fffbfe);
}

.submit-button {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}

.submit-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a43c 0%, #008836 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error Toast */
.error-toast {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--error, #b3261e);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 100;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}
</style>

<!-- Global styles for Toast (not scoped) -->
<style>
/* Success Toast - Minimal and Elegant */
.status-success-toast-minimal {
  background: linear-gradient(135deg, #00c444 0%, #00a83a 100%) !important;
  border-radius: 16px !important;
  padding: 16px 24px !important;
  box-shadow: 0 8px 32px rgba(0, 196, 68, 0.35) !important;
  border: none !important;
  min-width: 280px !important;
  max-width: 400px !important;
}

.status-toast-title {
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.5 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.status-toast-icon {
  border: none !important;
  margin: 0 12px 0 0 !important;
  width: 28px !important;
  height: 28px !important;
}

.status-toast-icon .swal2-icon-content {
  font-size: 18px !important;
}

/* Custom checkmark icon styling */
.status-success-toast-minimal .swal2-success-ring {
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.status-success-toast-minimal .swal2-success-line-tip,
.status-success-toast-minimal .swal2-success-line-long {
  background-color: #ffffff !important;
}

.status-success-toast-minimal .swal2-success-fix {
  background-color: transparent !important;
}

/* Animation */
.status-toast-animate-in {
  animation: statusToastSlideIn 0.4s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
}

.status-toast-animate-out {
  animation: statusToastSlideOut 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
}

@keyframes statusToastSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes statusToastSlideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
}

/* RTL Support */
[dir="rtl"] .status-success-toast-minimal {
  direction: rtl;
}

[dir="rtl"] .status-toast-icon {
  margin: 0 0 0 12px !important;
}
</style>
